import { Component, computed, effect, inject, Signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ComponentsModule, DialogService, SnackBarService, TabItem } from '@mastechnicaegroup/carelinelive-ui';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';
import { AsyncPipe } from '@angular/common';
import { UserService } from '@lib/user/user.service';
import { SystemAdmin, User } from '@lib/api/models/user';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { getDisplayName } from '@lib/api/models/person';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { AddSystemAdminDialogComponent } from '../../dialogs/add/add-system-admin-dialog.component';
import { PromptSystemAdminRemoveDialogComponent } from '../../dialogs/prompt/prompt-system-admin-remove-dialog.component';
import { postParamNamesFilterActive, postParamNamesFilterQuery } from '@shared/filters/filter-params';

@Component({
    selector: 'app-setting-system-admins-index-page',
    imports: [ComponentsModule, AsyncPipe, PaginationCardComponent, RouterLink, ReactiveFormsModule, HeaderComponent, ErrorPanelComponent],
    templateUrl: './setting-system-admins-index-page.component.html',
    standalone: true,
    styleUrl: './setting-system-admins-index-page.component.scss'
})
export class SettingSystemAdminsIndexPageComponent {

    private readonly _activeRoute = inject(ActivatedRoute);
    private readonly _userService = inject(UserService);
    private readonly _router = inject(Router);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _dialog = inject(DialogService);
    private readonly _authService = inject(AuthenticationService);
    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);
    protected readonly getDisplayName = getDisplayName;

    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '0',
            title: 'All'
        },
        {
            disabled: false,
            id: '1',
            title: 'Active'
        },
        {
            disabled: false,
            id: '2',
            title: 'Inactive'
        }
    ];

    users?: Record<string, string | number>[];

    protected user = toSignal(this._authService.user$);

    protected currentTabId$ = this._activeRoute.queryParams.pipe(
        map((params): string => params['tab'] ?? '0'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    /** The search form control */
    protected readonly searchControl = new FormControl<string>(
        this._activeRoute.snapshot.queryParams['q'] ?? '',
        [Validators.maxLength(50)]
    );

    /** An observable that emits the search query */
    protected readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),

        // Only fire once every 100 ms
        debounceTime(100),

        // If it's an empty string, return null
        map(value => value ? value : undefined)
    ));

    /** The current page being shown */
    protected readonly page = toSignal<number>(this._activeRoute.queryParams.pipe(
        map((params): number => params['page'] ?? 1)
    ));

    /** Fetch the page of users */
    protected readonly pageResult: Signal<Result<PaginatedApiResponse<User>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        searchQuery: toObservable(this.searchQuery),
        tabId: this.currentTabId$,
        refresh: this.refreshTrigger$.asObservable()
    }).pipe(
        switchMap(({ page, searchQuery, tabId }) => this._userService.getAdminUsers(page, {
            [postParamNamesFilterQuery]: searchQuery,
            [postParamNamesFilterActive]: tabId === '0' ? '' : tabId === '1' ? 'true' : 'false'
        }).pipe(
            // Turn into a result
            toResult()
        )),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta-data from the page result
        return this.pageResult()?.value?.meta;
    });

    constructor() {
        // Update the query params when the page or search query changes
        effect(() => {
            this._router.navigate([], {
                relativeTo: this._activeRoute,
                queryParams: {
                    q: this.searchQuery()
                },
                queryParamsHandling: 'merge',

                // Don't push a new history state, just replace the query param
                replaceUrl: true
            });
        });
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }

    async currentTabIdChange($event: string) {
        await this._router.navigate([], {
            queryParams: { tab: $event },
            queryParamsHandling: 'merge'
        });
    }

    addSystemAdmin() {
        return AddSystemAdminDialogComponent.show(this._dialog).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'User status failed to change.',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }

    removeAdmin(user: SystemAdmin) {
        return PromptSystemAdminRemoveDialogComponent.show(this._dialog, user).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {

                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'System admin failed to be removed.',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });

                return of(null);
            })
        ).subscribe();
    }
}
